/**
 * theme/tokens.ts —— **样式令牌词表与字阶**（壳规范 §1ter·三／二）。
 *
 * 定位：色／间距／字号／状态四态／遮罩的**唯一名族**；**令牌真源＝`styles/tokens/` 目录**（唯一处）。
 * ★F-18（册 §6.7）：theme 对令牌源**只读引用**（本件只登记名字与元数据，不声明/不覆盖令牌值）。
 * 纪律：**禁硬编码色值**（判据 `ui_token_usage_declared`／`ui_mobile_tokens_used`）；
 *      本件只登记**名字**与**元数据**，不写任何色值。
 */

/** 颜色/表面/文本/描边/主色 token 名（§1ter·三；**旧树真名**，A5 收口后与令牌真源 `styles/tokens/` 同名同集） */
export const COLOR_TOKENS = [
  "surface-base", "surface-raised", "surface-overlay", "surface-input", "surface-sunken",
  "text-primary", "text-secondary", "text-tertiary", "muted", "text-inverse", "text-link",
  "border-base", "border-subtle", "border-strong", "border-focus",
  "accent", "blue-100",
  "interactive-primary", "interactive-hover", "interactive-active", "interactive-disabled",
  "state-info", "state-success", "state-warning", "state-danger",
  "surface-scrim", "alpha-black-4",
] as const;

/** 状态四态＋空闲（§1ter·三：**每态须文本等价**） */
export const STATE_TOKENS = ["running", "ok", "warn", "error", "idle"] as const;
export type StateToken = (typeof STATE_TOKENS)[number];

/** 状态文本等价（状态点不得只靠颜色编码） */
export const STATE_LABELS: Record<StateToken, string> = {
  running: "进行中", ok: "正常", warn: "告警", error: "出错", idle: "空闲",
};

/**
 * 状态词表 → 样式令牌真名（A5 收口：词表是**语义**，令牌层用旧树真名）。
 * `--xg-state-<名>` 的定义集＝本表的值集（判据 `ui_token_names_canonical` 按本表断言）。
 */
export const STATE_TOKEN_VARS: Record<StateToken, string> = {
  running: "info", ok: "success", warn: "warning", error: "danger", idle: "idle",
};

/** 间距 token 名（真名 7 档；4pt 基；§1ter·一） */
export const SPACE_TOKENS = ["2xs", "xs", "sm", "md", "lg", "xl", "2xl"] as const;

/** 字号 token 名（真名 4 档；§1ter·二） */
export const FONT_TOKENS = ["content", "secondary", "caption", "code"] as const;

/** 版面几何 token 名（§1 三栏几何） */
export const LAYOUT_TOKENS = [
  "sidebar-min", "sidebar-default", "sidebar-max", "sidebar-collapsed",
  "center-min", "rightbar-min", "topbar-h", "tabbar-h", "input-min-h", "hit-target",
] as const;

/** 正文可调区间（§7：12–17，默认 14） */
export const FONT_SIZE_RANGE: readonly [number, number] = [12, 17];
export const FONT_SIZE_DEFAULT = 14;
/** 行高比（正文 14／24（D3·L-UI-02）⇒ 其余字号按同比取整，保持字阶一致） */
const LINE_HEIGHT_RATIO = 24 / 14;

export function clampFontSize(size: number): number {
  const [min, max] = FONT_SIZE_RANGE;
  if (!Number.isFinite(size)) return FONT_SIZE_DEFAULT;
  return Math.min(max, Math.max(min, Math.round(size)));
}

/** 字号 ⇒ 行高（确定性取整；同字号同结果） */
export function lineHeightFor(size: number): number {
  return Math.round(clampFontSize(size) * LINE_HEIGHT_RATIO);
}

/** token 引用（组件样式只许经此出值；禁写色值/尺寸字面量） */
export function tokenVar(name: string): string {
  return "var(--xg-" + name + ")";
}

/** 令牌名 → CSS 变量名 */
export function cssVar(name: string): string {
  return "--xg-" + name;
}

/**
 * 硬编码色值探针（自证用）：命中 CSS 颜色字面量（井号十六进制／rgb 三元组／hsl 三元组）⇒ 返回命中文本。
 * 用途：测试对 `web/src` 全量扫描，确保色值只出现在 token 层。
 */
export function findHardcodedColors(source: string): string[] {
  const pattern = /#[0-9a-fA-F]{3,8}\b|\brgba?\(|\bhsla?\(/g;
  return source.match(pattern) ?? [];
}
